<script>
  import { createEventDispatcher } from 'svelte';
  import { getApiUrl } from '../api';
  import { selectedCountry } from '../countryStore';
  import { t, currentLang } from '../i18nStore';

  export let showModal = false;
  export let preSelectedCards = [];
  export let locale = 'en';

  const dispatch = createEventDispatcher();

  let allCards = [];
  let banks = [];
  let issuers = [];
  let filteredCards = [];
  
  let selectedCards = [];
  let selectedBank = '';
  let selectedIssuer = '';
  let searchQuery = '';
  let customCardName = '';

  let isLoading = true;
  let error = '';
  let wasOpen = false;

  $: if (showModal) {
    if (!wasOpen) {
      wasOpen = true;
      initialize();
    }
  } else {
    wasOpen = false;
  }

  async function initialize() {
    isLoading = true;
    error = '';
    selectedCards = [...preSelectedCards];
    selectedBank = '';
    selectedIssuer = '';
    searchQuery = '';
    
    try {
      // Fetch all cards for this country to build the filters locally
      const response = await fetch(getApiUrl(`/credit-cards/list?countryCode=${$selectedCountry}&pageSize=1000`));
      if (!response.ok) throw new Error(t('cc_modal.no_cards'));
      
      const data = await response.json();
      allCards = data.cards || [];
      
      // Extract unique banks and issuers for filters
      banks = [...new Set(allCards.map(c => c.bankName))].sort();
      issuers = [...new Set(allCards.map(c => c.issuer))].sort();
      
      filterCards();
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  // Reactive filtering logic
  $: if (selectedBank || selectedIssuer || searchQuery || allCards.length >= 0) {
    filterCards();
  }

  function filterCards() {
    filteredCards = allCards.filter(card => {
      const matchBank = !selectedBank || card.bankName === selectedBank;
      const matchIssuer = !selectedIssuer || card.issuer === selectedIssuer;
      const matchSearch = !searchQuery || 
        card.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        card.bankName.toLowerCase().includes(searchQuery.toLowerCase());
      return matchBank && matchIssuer && matchSearch;
    });
  }

  function toggleSelection(cardName) {
    if (selectedCards.includes(cardName)) {
      selectedCards = selectedCards.filter(name => name !== cardName);
    } else {
      selectedCards = [...selectedCards, cardName];
    }
  }

  function addCustomCard() {
    const trimmed = customCardName.trim();
    if (trimmed && !selectedCards.includes(trimmed)) {
      selectedCards = [...selectedCards, trimmed];
      customCardName = '';
    }
  }

  function removeCard(name) {
    selectedCards = selectedCards.filter(c => c !== name);
  }

  function confirmSelection() {
    dispatch('confirm', { selected: selectedCards });
    closeModal();
  }

  function closeModal() {
    dispatch('close');
  }
</script>

{#if showModal}
  <div class="modal-backdrop" on:click={closeModal}>
    <div class="modal-content" on:click|stopPropagation>
      <div class="modal-header">
        <h3>{$t('cc_modal.title')} ({$selectedCountry})</h3>
        <button class="close-icon" on:click={closeModal}>&times;</button>
      </div>

      <div class="filters-section">
        <div class="filter-row">
          <select bind:value={selectedBank}>
            <option value="">{$t('cc_modal.all_banks')}</option>
            {#each banks as bank}
              <option value={bank}>{bank}</option>
            {/each}
          </select>

          <select bind:value={selectedIssuer}>
            <option value="">{$t('cc_modal.all_issuers')}</option>
            {#each issuers as issuer}
              <option value={issuer}>{issuer}</option>
            {/each}
          </select>
        </div>
        <input 
          type="text" 
          class="search-bar" 
          placeholder={$t('cc_modal.search_placeholder')} 
          bind:value={searchQuery} 
        />
      </div>

      <div class="list-container">
        {#if isLoading}
          <div class="status-msg">{$t('common.loading')}</div>
        {:else if error}
          <div class="status-msg error">{error}</div>
        {:else if filteredCards.length > 0}
          <div class="card-grid">
            {#each filteredCards as card (card._id)}
              <label class="card-toggle" class:active={selectedCards.includes(card.name)}>
                <input 
                  type="checkbox" 
                  checked={selectedCards.includes(card.name)} 
                  on:change={() => toggleSelection(card.name)} 
                />
                <div class="card-info">
                  <span class="bank-tag">{card.bankName}</span>
                  <span class="card-name">{card.name}</span>
                  <span class="issuer-tag">{card.issuer}</span>
                </div>
              </label>
            {/each}
          </div>
        {:else}
          <div class="status-msg">{$t('cc_modal.no_cards')}</div>
        {/if}
      </div>

      <div class="footer-sections">
        <!-- Custom Input -->
        <div class="custom-entry">
          <label>{$t('cc_modal.add_custom')}</label>
          <div class="input-group">
            <input 
              type="text" 
              bind:value={customCardName} 
              placeholder={$t('cc_modal.custom_placeholder')}
              on:keydown={(e) => e.key === 'Enter' && addCustomCard()}
            />
            <button on:click={addCustomCard} disabled={!customCardName.trim()}>{$t('cc_modal.add_btn')}</button>
          </div>
        </div>

        <!-- Summary -->
        {#if selectedCards.length > 0}
          <div class="summary">
            <strong>{$t('cc_modal.selected_count')} ({selectedCards.length}):</strong>
            <div class="tag-cloud">
              {#each selectedCards as name}
                <span class="tag">
                  {name}
                  <button on:click={() => removeCard(name)}>&times;</button>
                </span>
              {/each}
            </div>
          </div>
        {/if}

        <div class="actions">
          <button class="btn-sec" on:click={closeModal}>{$t('common.cancel')}</button>
          <button class="btn-pri" on:click={confirmSelection}>{$t('cc_modal.confirm_btn')}</button>
        </div>
      </div>
    </div>
  </div>
{/if}

<style>
  .modal-backdrop {
    position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
    background: rgba(0,0,0,0.5); z-index: 2000;
    display: flex; align-items: center; justify-content: center;
  }
  .modal-content {
    background: white; width: 95%; max-width: 650px; height: 90vh;
    border-radius: 12px; display: flex; flex-direction: column; overflow: hidden;
  }
  .modal-header {
    padding: 1rem 1.5rem; border-bottom: 1px solid #eee;
    display: flex; justify-content: space-between; align-items: center;
  }
  .modal-header h3 { margin: 0; font-size: 1.2rem; }
  .close-icon { background: none; border: none; font-size: 1.8rem; cursor: pointer; color: #999; }

  .filters-section { padding: 1rem 1.5rem; background: #fcfcfc; border-bottom: 1px solid #eee; }
  .filter-row { display: grid; grid-template-columns: 1fr 1fr; gap: 0.75rem; margin-bottom: 0.75rem; }
  select, .search-bar { 
    width: 100%; padding: 0.6rem; border: 1px solid #ddd; border-radius: 6px; font-size: 0.95rem; 
  }

  .list-container { flex: 1; overflow-y: auto; padding: 1rem 1.5rem; }
  .status-msg { text-align: center; padding: 2rem; color: #666; }
  .status-msg.error { color: #dc3545; }

  /* Desktop List View */
  .card-grid { display: flex; flex-direction: column; gap: 0.5rem; }
  .card-toggle {
    display: flex; align-items: center; padding: 0.75rem; border: 1px solid #eee;
    border-radius: 8px; cursor: pointer; transition: all 0.2s;
  }
  .card-toggle input { width: 1.2rem; height: 1.2rem; margin-right: 1rem; }
  .card-info { display: flex; flex-direction: column; flex: 1; }
  .bank-tag { font-size: 0.75rem; color: #007bff; font-weight: bold; text-transform: uppercase; }
  .card-name { font-weight: 600; margin: 2px 0; }
  .issuer-tag { font-size: 0.75rem; color: #666; }

  /* Mobile Grid View with Toggle Buttons */
  @media (max-width: 600px) {
    .card-grid {
      display: grid; grid-template-columns: repeat(2, 1fr); gap: 0.5rem;
    }
    .card-toggle {
      flex-direction: column; text-align: center; padding: 0.5rem;
      justify-content: center; min-height: 80px;
    }
    .card-toggle input { display: none; }
    .card-toggle.active { background: #007bff; border-color: #007bff; color: white; }
    .card-toggle.active .bank-tag, .card-toggle.active .issuer-tag { color: rgba(255,255,255,0.8); }
    
    .custom-entry, .summary { display: none; } /* Hide sections to save space as requested */
    .filter-row { grid-template-columns: 1fr; }
  }

  .footer-sections { padding: 1rem 1.5rem; border-top: 1px solid #eee; background: #fff; }
  .custom-entry label { font-size: 0.85rem; font-weight: bold; margin-bottom: 0.4rem; display: block; }
  .input-group { display: flex; gap: 0.5rem; margin-bottom: 1rem; }
  .input-group input { flex: 1; padding: 0.5rem; border: 1px solid #ddd; border-radius: 4px; }
  .input-group button { background: #28a745; color: white; border: none; padding: 0 1rem; border-radius: 4px; cursor: pointer; }
  .input-group button:disabled { background: #ccc; }

  .summary { margin-bottom: 1rem; }
  .tag-cloud { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.5rem; max-height: 80px; overflow-y: auto; }
  .tag { 
    background: #f0f2f5; padding: 0.2rem 0.6rem; border-radius: 20px; font-size: 0.8rem;
    display: flex; align-items: center; gap: 4px;
  }
  .tag button { background: none; border: none; color: #dc3545; font-weight: bold; cursor: pointer; padding: 0; line-height: 1; }

  .actions { display: flex; justify-content: flex-end; gap: 0.75rem; }
  .btn-pri, .btn-sec { padding: 0.7rem 1.2rem; border-radius: 6px; border: none; cursor: pointer; font-size: 1rem; font-weight: 500; }
  .btn-pri { background: #007bff; color: white; }
  .btn-sec { background: #f0f0f0; color: #333; }
</style>